import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { HELP_CONTENT } from "@/lib/help-content";

interface AppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId?: string;
  prefilledContactId?: string;
  prefilledConversationId?: string;
  onSuccess?: () => void;
}

export function AppointmentDialog({
  open,
  onOpenChange,
  appointmentId,
  prefilledContactId,
  prefilledConversationId,
  onSuccess,
}: AppointmentDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contactId, setContactId] = useState(prefilledContactId || "");
  const [agentId, setAgentId] = useState("");
  const [appointmentType, setAppointmentType] = useState("other");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [startDate, setStartDate] = useState<Date>();
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  
  // Duration management
  const [durationMinutes, setDurationMinutes] = useState<number>(30);
  const [customDuration, setCustomDuration] = useState<string>("");
  const [isCustomDuration, setIsCustomDuration] = useState(false);
  const [orgTimezone, setOrgTimezone] = useState<string>("America/New_York");
  
  // Reminder intervals in minutes
  const [reminder5min, setReminder5min] = useState(false);
  const [reminder15min, setReminder15min] = useState(false);
  const [reminder1hr, setReminder1hr] = useState(true);
  const [reminder24hr, setReminder24hr] = useState(true);
  const [sendCustomerReminders, setSendCustomerReminders] = useState(true);
  const [sendAgentReminders, setSendAgentReminders] = useState(true);

  const durationOptions = [
    { label: "10 minutes", value: 10 },
    { label: "20 minutes", value: 20 },
    { label: "30 minutes", value: 30 },
    { label: "45 minutes", value: 45 },
    { label: "1 hour", value: 60 },
    { label: "1.5 hours", value: 90 },
    { label: "2 hours", value: 120 },
    { label: "3 hours", value: 180 },
    { label: "4 hours", value: 240 },
  ];


  // Auto-calculate end time when start time or duration changes
  useEffect(() => {
    if (startTime && durationMinutes) {
      const [hour, min] = startTime.split(":").map(Number);
      const totalMinutes = hour * 60 + min + durationMinutes;
      const endHour = Math.floor(totalMinutes / 60) % 24;
      const endMin = totalMinutes % 60;
      const calculatedEndTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
      setEndTime(calculatedEndTime);
    }
  }, [startTime, durationMinutes]);

  useEffect(() => {
    if (open) {
      fetchContacts();
      fetchAgents();
      fetchOrgSettings();
      if (appointmentId) {
        fetchAppointment();
      } else {
        resetForm();
      }
    }
  }, [open, appointmentId]);

  const fetchOrgSettings = async () => {
    const { data } = await supabase
      .from('organization_settings')
      .select('timezone')
      .maybeSingle();
    if (data?.timezone) {
      setOrgTimezone(data.timezone);
    }
  };

  const fetchContacts = async () => {
    const { data } = await supabase
      .from("crm_contacts")
      .select("id, name, phone_number")
      .order("name");
    if (data) setContacts(data);
  };

  const fetchAgents = async () => {
    const { data } = await supabase
      .from("profiles")
      .select("id, full_name")
      .order("full_name");
    if (data) setAgents(data);
  };

  const fetchAppointment = async () => {
    if (!appointmentId) return;
    const { data, error } = await supabase
      .from("appointments")
      .select("*")
      .eq("id", appointmentId)
      .single();
    
    if (error) {
      toast({ title: "Error loading appointment", variant: "destructive" });
      return;
    }

    if (data) {
      setTitle(data.title);
      setDescription(data.description || "");
      setContactId(data.crm_contact_id);
      setAgentId(data.assigned_agent_id || "");
      setAppointmentType(data.appointment_type);
      setLocation(data.location || "");
      setNotes(data.notes || "");
      
      const start = new Date(data.start_time);
      setStartDate(start);
      setStartTime(format(start, "HH:mm"));
      
      const end = new Date(data.end_time);
      setEndTime(format(end, "HH:mm"));
      
      // Set duration if available, otherwise calculate from times
      if (data.duration_minutes) {
        setDurationMinutes(data.duration_minutes);
        setIsCustomDuration(false);
      }
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setContactId(prefilledContactId || "");
    setAgentId("");
    setAppointmentType("other");
    setLocation("");
    setNotes("");
    setStartDate(undefined);
    setStartTime("09:00");
    setEndTime("10:00");
    setDurationMinutes(30);
    setCustomDuration("");
    setIsCustomDuration(false);
    setReminder5min(false);
    setReminder15min(false);
    setReminder1hr(true);
    setReminder24hr(true);
    setSendCustomerReminders(true);
    setSendAgentReminders(true);
  };

  const handleSave = async () => {
    if (!title || !contactId || !startDate) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }

    setLoading(true);

    try {
      const [startHour, startMin] = startTime.split(":").map(Number);
      const [endHour, endMin] = endTime.split(":").map(Number);
      
      const startDateTime = new Date(startDate);
      startDateTime.setHours(startHour, startMin, 0, 0);
      
      const endDateTime = new Date(startDate);
      endDateTime.setHours(endHour, endMin, 0, 0);

      const { data: { user } } = await supabase.auth.getUser();
      
      // Build reminder intervals array based on selections
      const reminderIntervals = [];
      if (reminder5min) reminderIntervals.push(5);
      if (reminder15min) reminderIntervals.push(15);
      if (reminder1hr) reminderIntervals.push(60);
      if (reminder24hr) reminderIntervals.push(1440);
      
      const appointmentData = {
        title,
        description,
        crm_contact_id: contactId,
        assigned_agent_id: agentId || null,
        appointment_type: appointmentType,
        location,
        notes,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        duration_minutes: durationMinutes,
        timezone: orgTimezone,
        conversation_id: prefilledConversationId || null,
        created_by: user?.id,
        reminder_intervals: reminderIntervals,
        send_customer_reminders: sendCustomerReminders,
        send_agent_reminders: sendAgentReminders,
      };

      let savedAppointmentId = appointmentId;

      if (appointmentId) {
        const { error } = await supabase
          .from("appointments")
          .update(appointmentData)
          .eq("id", appointmentId);
        
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("appointments")
          .insert(appointmentData)
          .select()
          .single();
        
        if (error) throw error;
        savedAppointmentId = data.id;

        // Send management link to customer if they have a phone number
        if (data.customer_token && contactId) {
          const { data: contact } = await supabase
            .from("crm_contacts")
            .select("phone_number")
            .eq("id", contactId)
            .single();

          if (contact?.phone_number) {
            const managementUrl = `${window.location.origin}/manage-appointment/${data.customer_token}`;
            const message = `Your appointment "${title}" is confirmed for ${format(startDateTime, "MMM d 'at' h:mm a")}. Manage your appointment: ${managementUrl}`;
            
            try {
              await supabase.functions.invoke('send-sms', {
                body: {
                  to: contact.phone_number,
                  message,
                }
              });
            } catch (smsError) {
              console.error('Error sending management link:', smsError);
              // Don't fail the appointment creation if SMS fails
            }
          }
        }
      }

      // Reminders are now created automatically by the database trigger

      toast({ title: appointmentId ? "Appointment updated" : "Appointment created" });
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast({ title: "Error saving appointment", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{appointmentId ? "Edit Appointment" : "Create Appointment"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Sales Demo"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact">Contact *</Label>
            <Select value={contactId} onValueChange={setContactId}>
              <SelectTrigger>
                <SelectValue placeholder="Select contact" />
              </SelectTrigger>
              <SelectContent>
                {contacts.map((contact) => (
                  <SelectItem key={contact.id} value={contact.id}>
                    {contact.name} ({contact.phone_number})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={appointmentType} onValueChange={setAppointmentType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="demo">Demo</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              Duration
              <HelpTooltip {...HELP_CONTENT.appointments.duration} />
            </Label>
            <RadioGroup
              value={isCustomDuration ? "custom" : String(durationMinutes)}
              onValueChange={(value) => {
                if (value === "custom") {
                  setIsCustomDuration(true);
                } else {
                  setIsCustomDuration(false);
                  setDurationMinutes(Number(value));
                }
              }}
            >
              <div className="grid grid-cols-2 gap-2">
                {durationOptions.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value={String(option.value)} 
                      id={`duration-${option.value}`} 
                    />
                    <Label 
                      htmlFor={`duration-${option.value}`} 
                      className="font-normal cursor-pointer"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
                
                <div className="col-span-2 flex items-center space-x-2">
                  <RadioGroupItem value="custom" id="duration-custom" />
                  <Label htmlFor="duration-custom" className="font-normal cursor-pointer">
                    Custom:
                  </Label>
                  <Input
                    type="number"
                    className="w-24"
                    placeholder="mins"
                    value={customDuration}
                    onChange={(e) => {
                      setCustomDuration(e.target.value);
                      const mins = Number(e.target.value);
                      if (mins > 0) {
                        setDurationMinutes(mins);
                      }
                    }}
                    onClick={() => setIsCustomDuration(true)}
                    disabled={!isCustomDuration}
                  />
                  <span className="text-sm text-muted-foreground">minutes</span>
                </div>
              </div>
            </RadioGroup>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime">Start Time</Label>
              <Input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endTime">End Time (calculated)</Label>
              <Input
                id="endTime"
                type="time"
                value={endTime}
                readOnly
                className="bg-muted/50 cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground">
                Automatically calculated from duration
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="agent">Assigned Agent</Label>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger>
                <SelectValue placeholder="Select agent (optional)" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.full_name || agent.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Office, Phone Call"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {!appointmentId && (
            <>
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  Reminder Times
                  <HelpTooltip {...HELP_CONTENT.appointments.reminders} />
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="reminder5min"
                      checked={reminder5min}
                      onCheckedChange={(checked) => setReminder5min(checked as boolean)}
                    />
                    <Label htmlFor="reminder5min" className="font-normal cursor-pointer">5 minutes before</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="reminder15min"
                      checked={reminder15min}
                      onCheckedChange={(checked) => setReminder15min(checked as boolean)}
                    />
                    <Label htmlFor="reminder15min" className="font-normal cursor-pointer">15 minutes before</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="reminder1hr"
                      checked={reminder1hr}
                      onCheckedChange={(checked) => setReminder1hr(checked as boolean)}
                    />
                    <Label htmlFor="reminder1hr" className="font-normal cursor-pointer">1 hour before</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="reminder24hr"
                      checked={reminder24hr}
                      onCheckedChange={(checked) => setReminder24hr(checked as boolean)}
                    />
                    <Label htmlFor="reminder24hr" className="font-normal cursor-pointer">1 day before</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Send Reminders To</Label>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sendCustomer"
                      checked={sendCustomerReminders}
                      onCheckedChange={(checked) => setSendCustomerReminders(checked as boolean)}
                    />
                    <Label htmlFor="sendCustomer" className="font-normal cursor-pointer">Customer</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sendAgent"
                      checked={sendAgentReminders}
                      onCheckedChange={(checked) => setSendAgentReminders(checked as boolean)}
                    />
                    <Label htmlFor="sendAgent" className="font-normal cursor-pointer">Assigned Agent</Label>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : appointmentId ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
